import React, { useCallback, useRef } from 'react';

import { token } from '@atlaskit/tokens';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, Box, xcss } from '@atlaskit/primitives';

import { useIsDisabledContext } from '../IsDisabledContext';
import type { UnsplashResult, SelectedImage } from '../index';

import { isUnsplashImage } from './unsplashAPI';
import { UnsplashImageThumbnail } from './Thumbnail/UnsplashImageThumbnail';
import { PlaceholderThumbnail } from './Thumbnail/PlaceholderThumbnail';
import { UploadedImageThumbnail } from './Thumbnail/UploadedImageThumbnail';
import { NoImageThumbnail } from './Thumbnail/NoImageThumbnail';
import { ExternalSiteImageThumbnail } from './Thumbnail/ExternalSiteImageThumbnail';

const externalSiteMatcher =
	/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

const ImagesWrapperStyles = xcss({
	marginTop: 'space.100',
	justifyContent: 'space-between',
	paddingTop: 'space.0',
});

const selectedImageTickStyles = xcss({
	position: 'absolute',
	bottom: 'space.0',
	right: 'space.0',

	'::before': {
		content: '""',
		backgroundColor: 'elevation.surface',
		height: '50%',
		width: '50%',
		position: 'absolute',
		right: 'space.050',
		top: 'space.050',
	},
});

const checkboxWrapperStyles = xcss({
	position: 'relative',
});

type UnsplashImage = {
	fullSizeImageUrl: string;
	altDescription?: string;
};

type CustomImage = {
	imageId: string;
};

enum ThumbnailType {
	UNSPLASH_DEFAULT_IMAGE,
	UNSPLASH_IMAGE,
	CUSTOM_IMAGE,
	EXTERNAL_SITE_IMAGE,
	NO_IMAGE,
}

type Thumbnail = {
	type: ThumbnailType;
	image: UnsplashResult | UnsplashImage | CustomImage | string | undefined;
};

const isUnsplashDefaultImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashResult | undefined } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_DEFAULT_IMAGE;
};

const isUnsplashImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashImage } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_IMAGE;
};

const isCustomImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: CustomImage } => {
	return thumbnail.type === ThumbnailType.CUSTOM_IMAGE;
};

const isExternalSiteImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: string } => {
	return thumbnail.type === ThumbnailType.EXTERNAL_SITE_IMAGE;
};

const isNoImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: undefined } => {
	return thumbnail.type === ThumbnailType.NO_IMAGE;
};

const createCustomImageThumbnail = (imageId: string): Thumbnail => ({
	type: ThumbnailType.CUSTOM_IMAGE,
	image: { imageId },
});

const createUnsplashDefaultImageThumbnail = (image?: UnsplashResult): Thumbnail => ({
	type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
	image,
});

const createUnsplashImageThumbnail = (url: string, altText?: string): Thumbnail => ({
	type: ThumbnailType.UNSPLASH_IMAGE,
	image: { fullSizeImageUrl: url, altDescription: altText },
});

const createExternalSiteImageThumbnail = (url: string): Thumbnail => ({
	type: ThumbnailType.EXTERNAL_SITE_IMAGE,
	image: url,
});

const createNoImageThumbnail = (): Thumbnail => ({
	type: ThumbnailType.NO_IMAGE,
	image: undefined,
});

const initialUnsplashResultsIncludes = ({ imageUrl, initialUnsplashResults, isNoImageAllowed }) => {
	if (!initialUnsplashResults?.length) {
		return false;
	}
	const adjustedUnsplashResults = isNoImageAllowed
		? initialUnsplashResults.toSpliced(0, 2)
		: initialUnsplashResults.toSpliced(0, 1);

	return adjustedUnsplashResults.map((result) => result.fullSizeImageUrl).includes(imageUrl);
};

const isSelectedImageUnsplashNotInInitialUnsplashResults = ({
	selectedImageUrl,
	initialUnsplashResults,
	isNoImageAllowed,
}) =>
	isUnsplashImage(selectedImageUrl) &&
	initialUnsplashResults?.length &&
	!initialUnsplashResultsIncludes({
		imageUrl: selectedImageUrl,
		initialUnsplashResults,
		isNoImageAllowed,
	});

export const renderImagePlaceholders = () => {
	return Array(5)
		.fill(null)
		.map((_, index) => <PlaceholderThumbnail key={index} />);
};

type DefaultImagePickerProps = {
	contentId: string;
	confluencePageId?: string;
	imageAltText?: string;
	selectedImageUrl?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	handleImageSelection: (props: SelectedImage) => void;
	analyticsSource: string;
	isUploadingImage: boolean;
	enablePaddingBottom?: boolean;
	isNoImageAllowed: boolean;
	previewUploadedImageId?: string;
};

export const DefaultImagePicker = ({
	contentId,
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	initialUnsplashResults,
	analyticsSource,
	isUploadingImage,
	isNoImageAllowed,
	previewUploadedImageId,
}: DefaultImagePickerProps) => {
	const isDisabled = useIsDisabledContext();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onImageClick = useCallback(
		({ fullSizeImageUrl, altDescription, downloadLink }: SelectedImage) => {
			if (!isUploadingImage) {
				handleImageSelection({
					fullSizeImageUrl,
					altDescription,
					downloadLink,
				});

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'selected',
						actionSubject: 'image',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplashDefault',
						},
					},
				}).fire();
			}
		},
		[analyticsSource, createAnalyticsEvent, handleImageSelection, isUploadingImage],
	);

	const firstImage = useRef<Thumbnail>();

	const renderCheckboxIcon = useCallback(() => {
		return (
			<Box xcss={selectedImageTickStyles}>
				<Flex xcss={checkboxWrapperStyles}>
					<CheckboxCheckedIcon label="checkmark" color={token('color.icon.brand')} />
				</Flex>
			</Box>
		);
	}, []);

	const renderGenericUnsplashImage = useCallback(
		({ smallSizeImageUrl, fullSizeImageUrl, downloadLink, description, isSelected, index }) => {
			return (
				<UnsplashImageThumbnail
					key={`thumbnail-${index}`}
					image={smallSizeImageUrl}
					description={description}
					isSelected={isSelected}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl,
							altDescription: description,
							downloadLink,
						})
					}
				>
					{isSelected && renderCheckboxIcon()}
				</UnsplashImageThumbnail>
			);
		},
		[isUploadingImage, onImageClick, renderCheckboxIcon],
	);

	const renderUnsplashDefaultImage = useCallback(
		(image: UnsplashResult, index: number) => {
			const { fullSizeImageUrl, altDescription, downloadLink, smallSizeImageUrl } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: smallSizeImageUrl ?? fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription,
				downloadLink,
				isSelected,
				index,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderUnsplashImage = useCallback(
		(image: UnsplashImage, index: number) => {
			const { fullSizeImageUrl, altDescription } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription ?? '',
				downloadLink: undefined,
				isSelected,
				index,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderExternalSiteImage = useCallback(
		(image: string, index: number) => {
			const isSelectedImage = image === selectedImageUrl;
			return (
				<ExternalSiteImageThumbnail
					key={`thumbnail-${index}`}
					image={image}
					isSelected={isSelectedImage}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: image,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && renderCheckboxIcon()}
				</ExternalSiteImageThumbnail>
			);
		},
		[selectedImageUrl, isUploadingImage, renderCheckboxIcon, onImageClick],
	);

	const renderUploadedImage = useCallback(
		(image: CustomImage, index: number) => {
			const { imageId } = image;
			const isSelectedImage =
				imageId === previewUploadedImageId || (imageId === selectedImageUrl && index === 0);

			return (
				<UploadedImageThumbnail
					key={`thumbnail-${index}`}
					contentId={contentId}
					imageId={imageId}
					isSelected={isSelectedImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: imageId,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && renderCheckboxIcon()}
				</UploadedImageThumbnail>
			);
		},
		[selectedImageUrl, contentId, onImageClick, renderCheckboxIcon, previewUploadedImageId],
	);

	const renderNoImageThumbnail = useCallback(
		(index: number) => {
			const isSelected = selectedImageUrl === '';
			return (
				<NoImageThumbnail
					key={index}
					isSelected={isSelected}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: '',
							altDescription: '',
						})
					}
				>
					{isSelected && renderCheckboxIcon()}
				</NoImageThumbnail>
			);
		},
		[selectedImageUrl, onImageClick, renderCheckboxIcon],
	);

	const getFirstImage = (): Thumbnail => {
		if (!firstImage.current) {
			if (previewUploadedImageId && !selectedImageUrl) {
				return createCustomImageThumbnail(previewUploadedImageId);
			} else if (
				selectedImageUrl &&
				isSelectedImageUnsplashNotInInitialUnsplashResults({
					selectedImageUrl,
					initialUnsplashResults,
					isNoImageAllowed,
				})
			) {
				return createUnsplashImageThumbnail(selectedImageUrl, imageAltText);
			} else if (initialUnsplashResults?.length) {
				return createUnsplashDefaultImageThumbnail(initialUnsplashResults[0]);
			}
		}

		if (previewUploadedImageId) {
			return createCustomImageThumbnail(previewUploadedImageId);
		}

		if (selectedImageUrl && !isUnsplashImage(selectedImageUrl)) {
			return externalSiteMatcher.test(selectedImageUrl)
				? createExternalSiteImageThumbnail(selectedImageUrl)
				: createCustomImageThumbnail(selectedImageUrl);
		}

		if (
			selectedImageUrl &&
			isSelectedImageUnsplashNotInInitialUnsplashResults({
				selectedImageUrl,
				initialUnsplashResults,
				isNoImageAllowed,
			})
		) {
			return createUnsplashImageThumbnail(selectedImageUrl, imageAltText);
		}

		if (firstImage.current && !firstImage.current.image && initialUnsplashResults?.length) {
			return createUnsplashDefaultImageThumbnail(initialUnsplashResults[0]);
		}
		return {
			// ThumbnailType can be anything but NO_IMAGE because of the renderThumbnail if condition order.
			// All we care about is that image is undefined so that we render the placeholder thumbnail.
			type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
			image: undefined,
		};
	};

	firstImage.current = getFirstImage();

	let secondImage: Thumbnail;
	if (
		previewUploadedImageId &&
		selectedImageUrl &&
		!isUnsplashImage(selectedImageUrl) &&
		previewUploadedImageId !== selectedImageUrl
	) {
		secondImage = createCustomImageThumbnail(selectedImageUrl);
	} else if (isNoImageAllowed) {
		secondImage = createNoImageThumbnail();
	} else {
		secondImage = createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[1]);
	}

	const thirdImage =
		isNoImageAllowed && secondImage?.type !== ThumbnailType.NO_IMAGE
			? createNoImageThumbnail()
			: createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[2]);

	const imagesToDisplay = [
		firstImage.current,
		secondImage,
		thirdImage,
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[3]),
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[4]),
	];

	const renderThumbnails = useCallback(
		(thumbnail: Thumbnail, index) => {
			// Ordering matters for first image rendering logic
			if (isNoImageThumbnail(thumbnail)) {
				return renderNoImageThumbnail(index);
			}

			if (!thumbnail.image) {
				return <PlaceholderThumbnail key={index} />;
			}

			if (isUnsplashDefaultImageThumbnail(thumbnail)) {
				return renderUnsplashDefaultImage(thumbnail.image, index);
			}

			if (isUnsplashImageThumbnail(thumbnail)) {
				return renderUnsplashImage(thumbnail.image, index);
			}

			if (isExternalSiteImageThumbnail(thumbnail)) {
				return renderExternalSiteImage(thumbnail.image, index);
			}

			if (isCustomImageThumbnail(thumbnail)) {
				return renderUploadedImage(thumbnail.image, index);
			}

			return <PlaceholderThumbnail key={index} />;
		},
		[
			renderNoImageThumbnail,
			renderUnsplashDefaultImage,
			renderUnsplashImage,
			renderUploadedImage,
			renderExternalSiteImage,
		],
	);

	return (
		<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
			{isDisabled ? renderImagePlaceholders() : imagesToDisplay.map(renderThumbnails)}
		</Flex>
	);
};
